import { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import PeopleIcon from "@mui/icons-material/People";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";

import { ThresholdSetupTab } from "./tabs/ThresholdSetupTab";
import { ThresholdApproverTab } from "./tabs/ThresholdApproverTab";
import { ConfirmationModal } from "../../../../../common/Modal";

export default function AmountThresholdModal({
  isOpen,
  onClose,
  moduleName,
  selectedData,
  onSave, // // MODIFIED: Recieving onSave from parent
  loading
}) {
  const [activeTab, setActiveTab] = useState("setup");
  const [showConfirm, setShowConfirm] = useState(false);

  const [formData, setFormData] = useState({ label: "", minAmount: 0, maxAmount: 0 });
  const [approvers, setApprovers] = useState([{ approverId: "", fullName: "" }]);
  const [errorState, setErrorState] = useState({ amounts: false, label: false, approvers: false, duplicate: false });

  useEffect(() => {
    if (isOpen) {
      if (selectedData) {
        setFormData({
          id: selectedData.id,
          label: selectedData.label || "",
          minAmount: parseFloat(selectedData.minAmount) || 0,
          maxAmount: parseFloat(selectedData.maxAmount) || 0,
        });
        setApprovers(selectedData.approvers?.length > 0 ? selectedData.approvers : [{ approverId: "", fullName: "" }]);
      } else {
        setFormData({ label: "", minAmount: 0, maxAmount: 0 });
        setApprovers([{ approverId: "", fullName: "" }]);
      }
      setErrorState({ amounts: false, label: false, approvers: false, duplicate: false });
      setActiveTab("setup");
    }
  }, [isOpen, selectedData]);

  const validate = () => {
    const ids = approvers.map(a => a.approverId).filter(Boolean);
    const hasDuplicates = new Set(ids).size !== ids.length;

    const errors = {
      label: !formData.label,
      amounts: parseFloat(formData.maxAmount) <= parseFloat(formData.minAmount),
      approvers: approvers.every(a => !a.approverId),
      duplicate: hasDuplicates
    };

    setErrorState(errors);
    return !errors.label && !errors.amounts && !errors.approvers && !errors.duplicate;
  };

  const handlePreSubmit = () => {
    if (validate()) {
      setShowConfirm(true);
    } else {
      if (errorState.label || errorState.amounts) setActiveTab("setup");
      else setActiveTab("approvers");
    }
  };

  const handleFinalSave = async () => {
    const payload = {
      ...formData,
      moduleName,
      approverIds: approvers.map((a) => a.approverId).filter(Boolean),
    };

    setShowConfirm(false);

    // // MODIFIED: Calling the parent's save function
    const result = await onSave(payload);

    if (result.success) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const tabClass = (tabName) =>
    `px-4 py-3 text-sm font-medium rounded-t-lg transition-colors border-b-2 cursor-pointer border-none bg-transparent ${activeTab === tabName ? "border-blue-500 text-blue-600 bg-blue-50" : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
    }`;

  return (
    <>
      <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-3 text-gray-700">
        <div className="relative flex h-auto max-h-[90vh] w-full flex-col rounded-lg bg-gray-50 shadow-xl md:w-[60%] lg:w-[50%]">

          <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-lg border-b border-gray-200 bg-white px-6 py-3">
            <h2 className="flex items-center space-x-2 text-xl font-semibold text-gray-800">
              <AccountBalanceWalletIcon className="text-blue-500" fontSize="small" />
              <span className="text-lg text-gray-600">Threshold Config: {moduleName}</span>
            </h2>
            <button onClick={onClose} className="border-none bg-transparent p-1 text-gray-400 hover:text-gray-700 cursor-pointer">
              <CloseIcon fontSize="medium" />
            </button>
          </div>

          <div className="border-b border-gray-200 bg-white pt-3">
            <div className="flex space-x-1 px-6">
              <button onClick={() => setActiveTab("setup")} className={tabClass("setup")}>
                <SettingsIcon fontSize="small" className="mr-1" /> Setup
              </button>
              <button onClick={() => setActiveTab("approvers")} className={tabClass("approvers")}>
                <PeopleIcon fontSize="small" className="mr-1" /> Approvers
              </button>
            </div>
          </div>

          {errorState.duplicate && activeTab === "approvers" && (
            <div className="mx-5 mt-3 bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-md text-xs font-bold">
              ⚠️ DUPLICATE DETECTED: An approver can only be assigned once to this threshold.
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-5">
            {activeTab === "setup" ? (
              <ThresholdSetupTab formData={formData} setFormData={setFormData} errorState={errorState} />
            ) : (
              <ThresholdApproverTab approvers={approvers} setApprovers={setApprovers} formData={formData} />
            )}
          </div>

          <div className="flex items-center justify-end border-t border-gray-200 bg-white px-5 py-4 shadow-sm">
            <div className="flex space-x-2">
              <button onClick={onClose} className="rounded border-none bg-gray-400 px-4 py-2 text-sm text-white hover:bg-gray-500 cursor-pointer shadow-sm transition-colors">
                Cancel
              </button>
              <button onClick={handlePreSubmit} disabled={loading} className="rounded border-none bg-blue-500 px-4 py-2 text-sm text-white hover:bg-blue-600 cursor-pointer disabled:opacity-50 shadow-sm transition-colors">
                {loading ? "Processing..." : "Save Config"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        visible={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleFinalSave}
        title="Confirm Configuration"
        message={`Are you sure you want to save the threshold rules for ${formData.label}?`}
        type="info"
        isLoading={loading}
        confirmText="Save"
      />
    </>
  );
}